import type { part } from "@/api/supplierService";
import dayjs from "dayjs";
import excel from "exceljs";
import { ALLPARTS_DATA_COLS } from "./constants";
import {
  fillHeaderInfo,
  findInsertRow,
  spliceRowsPreservingMerges,
  applyMergesAndBorders,
  triggerDownload,
} from "./sheetHelpers";
import { buildExchangeRatePartRows, buildPartRows } from "./rowBuilders";
import type { exchangeRatePart } from "@/api/breakdownExchangeRateService";

interface ExportAllPartToExcelParams {
  supplierName: string;
  supplierCode: string;
  buyerCode: string;
  adminUnitType: string;
  profitUnitType: string;
  parts: part[];
}

export const exportAllPartToExcel = async ({
  supplierName,
  supplierCode,
  buyerCode,
  adminUnitType,
  profitUnitType,
  parts,
}: ExportAllPartToExcelParams) => {
  const fileName = `supplier_1st_${supplierCode}_${dayjs().format("YYYYMMDD_HHmmss")}`;
  const response = await fetch("/templates/template_export_supplier_1st.xlsx");
  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());

  const sheet = workbook.getWorksheet("CBD");
  if (!sheet) throw new Error("Sheet 'CBD' not found");

  fillHeaderInfo(sheet, {
    "Supplier ID:": supplierCode,
    "Supplier Name:": supplierName,
    "Buyer ID:": buyerCode,
    "Admin:": adminUnitType,
    "Profit:": profitUnitType,
  });

  const insertRow = findInsertRow(sheet, "Part No.", 1);
  if (insertRow === -1) {
    console.warn("Could not find 'Part No.' header — check template");
    return;
  }

  const rowsToInsert = await buildPartRows(parts);
  spliceRowsPreservingMerges(sheet, insertRow, rowsToInsert);
  applyMergesAndBorders(
    sheet,
    [],
    insertRow,
    rowsToInsert.length,
    ALLPARTS_DATA_COLS,
  );

  triggerDownload(await workbook.xlsx.writeBuffer(), fileName);
};

interface ExportAllExchangeRatePartToExcelParams {
  supplierName: string;
  supplierCode: string;
  buyerCode: string;
  parts: exchangeRatePart[];
}

export const exportAllExchangeRatePartToExcel = async ({
  supplierName,
  supplierCode,
  buyerCode,
  parts,
}: ExportAllExchangeRatePartToExcelParams) => {
  const fileName = `supplier_1st_${supplierCode}_${dayjs().format("YYYYMMDD_HHmmss")}`;
  const response = await fetch("/templates/template_export_exchange_rate.xlsx");
  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());

  const sheet = workbook.getWorksheet("CBD");
  if (!sheet) throw new Error("Sheet 'CBD' not found");

  fillHeaderInfo(sheet, {
    "Supplier ID:": supplierCode,
    "Supplier Name:": supplierName,
    "Buyer ID:": buyerCode,
  });

  const insertRow = findInsertRow(sheet, "Part No.", 1);
  if (insertRow === -1) {
    console.warn("Could not find 'Part No.' header — check template");
    return;
  }

  const rowsToInsert = await buildExchangeRatePartRows(parts);
  spliceRowsPreservingMerges(sheet, insertRow, rowsToInsert);
  applyMergesAndBorders(
    sheet,
    [],
    insertRow,
    rowsToInsert.length,
    ALLPARTS_DATA_COLS,
  );

  triggerDownload(await workbook.xlsx.writeBuffer(), fileName);
};
